import { useCallback, useMemo } from 'react';

const DISABLED = 'data-scroll-disabled';
const IGNORE_KEYS = new Set(['ArrowDown', 'ArrowUp']);
const OPTIONS = { passive: false };
const DEFAULT_ELEMENT: HTMLElement =
  document.querySelector('#root') || document.body;

const preventDefault = (event: Event) => event.preventDefault();

const ignoreKeys = (event: KeyboardEvent) => {
  if (IGNORE_KEYS.has(event.key)) {
    preventDefault(event);
  }
};

const setEnabled = (element: HTMLElement, enable: boolean) => {
  // is it already set?
  if (enable === !element.hasAttribute(DISABLED)) {
    return;
  }

  if (enable) {
    element.removeEventListener('wheel', preventDefault);
    element.removeEventListener('keydown', ignoreKeys);
    element.removeAttribute(DISABLED);
  } else {
    element.addEventListener('wheel', preventDefault, OPTIONS);
    element.addEventListener('keydown', ignoreKeys, OPTIONS);
    element.setAttribute(DISABLED, '');
  }
};

/**
 * Enables/disables scrolling on a DOM element. Ignores repeated calls.
 * @param element {HTMLElement} DOM element. Defaults to #root, or if that's missing, the body.
 * @returns {object} Object with enableScroll and disableScroll functions.
 */
export const useScrollControl = (element: HTMLElement = DEFAULT_ELEMENT) => {
  const enableScroll = useCallback(() => setEnabled(element, true), [element]);

  const disableScroll = useCallback(
    () => setEnabled(element, false),
    [element]
  );

  return useMemo(
    () => ({
      enableScroll,
      disableScroll,
    }),
    [enableScroll, disableScroll]
  );
};
